import React from 'react';
import { ColDef, ColumnState, ColGroupDef, ITooltipParams } from 'ag-grid-community';
import {
  BooleanColDef,
  CalculatedChangePercentColDef,
  ChangeColDef,
  ChangePercentColDef,
  ChangeShortenedNumberColDef,
  CommaSeparatedNumberColDef,
  DateColDef,
  DateTimeColDef,
  FlagColDef,
  LinkColDef,
  NotesColDef,
  NumberColDef,
  PercentNumberBackgroundColorColDef,
  PercentNumberColDef,
  PeriodColDef,
  PriceAlertColDef,
  PriceColDef,
  ProgressColDef,
  SetColDef,
  ShortenedNumberColDef,
  SparkLineColDef,
  SymbolColDef,
  TextColDef,
} from './columnDefs';
import { useScannerColumnableDefs } from '@benzinga/scanner-manager-hooks';
import { TimeConfig, useTime } from '@benzinga/time-manager-hooks';
import { isTextOverflowingAgGridCell } from './helpers/getCellTextOverflowed';
import { TimeFormat } from '@benzinga/date-utils';
import { sparklineTimeFrames } from '../Sparkline/Sparkline';
import { FullDataField } from '@benzinga/quotes-v3-fields-manager';
import { arrayDeepEqual } from '@benzinga/utils';

interface ColConfig {
  callback?: (value: boolean) => void;
  groupPrefix?: string;
  watchlistId?: string;
}

export const useLayoutFill = (colLayouts: ColumnState[]): ColumnState[] => {
  const addDefs = useQuoteColDef([]);

  const defs = React.useMemo(() => addDefs.flatMap<ColDef>(a => (a as ColGroupDef).children ?? [a]), [addDefs]);

  const addDefaults = React.useCallback((cs: ColumnState) => {
    return {
      aggFunc: null,
      flex: null,
      pinned: null,
      pivot: false,
      pivotIndex: null,
      rowGroup: false,
      rowGroupIndex: null,
      sort: null,
      sortIndex: null,
      width: 100,
      ...cs,
    };
  }, []);

  return React.useMemo(() => {
    const layoutMap = new Map(colLayouts.map(c => [c.colId, addDefaults(c)]));

    defs.forEach(df => {
      const id = df.colId ?? df.field ?? '';
      if (!layoutMap.has(id)) {
        layoutMap.set(
          id,
          addDefaults({
            colId: id,
            hide: df.hide ?? true,
          }),
        );
      }
    });

    return Array.from(layoutMap.values());
  }, [defs, colLayouts, addDefaults]);
};

export const useQuoteColDef = (colLayouts: ColumnState[], config?: ColConfig) => {
  const latestTimeConfig: TimeConfig = useTime();
  const defs = useScannerColumnableDefs();
  const buildPorfolioColumns = React.useCallback(
    (baseField: string) => `portfolio${baseField}${config?.watchlistId ?? 'Total'}`,
    [config?.watchlistId],
  );
  const sanitizeColumnState = React.useCallback(
    (cs?: ColumnState): Partial<ColDef> => ({
      aggFunc: cs?.aggFunc ?? undefined,
      colId: cs?.colId ?? '',
      flex: cs?.flex ?? undefined,
      // Default hide when layout entry is missing — scanner fields are opt-in.
      // hide:false here made every async field visible until layout caught up,
      // and the first column-resize persist flashed the whole grid.
      hide: cs?.hide ?? true,
      pinned: cs?.pinned ?? undefined,
      pivot: cs?.pivot ?? undefined,
      pivotIndex: cs?.pivotIndex ?? undefined,
      rowGroup: cs?.rowGroup ?? undefined,
      rowGroupIndex: cs?.rowGroupIndex ?? undefined,
      sort: cs?.sort ?? undefined,
      sortIndex: cs?.sortIndex ?? undefined,
      width: cs?.width ?? undefined,
    }),
    [],
  );

  const getColDefs = React.useCallback(
    (colLayouts: ColumnState[], _prevCols?: (ColGroupDef | ColDef)[]) => {
      const newCols = [
        ...Array.from(
          (defs ?? [])
            .reduce((acc, df) => {
              const layout = colLayouts.find(col => col.colId === df.name);
              const colDef = buildColDef(df, { ...sanitizeColumnState(layout), colId: df.name }, latestTimeConfig);
              colDef.colId = df.name;
              if (acc.has(df.category ?? 'NONE')) {
                acc.get(df.category ?? 'NONE')?.push(colDef);
              } else {
                acc.set(df.category ?? 'NONE', [colDef]);
              }
              return acc;
            }, new Map<string, ColDef[]>())
            .entries(),
        ).flatMap(([category, val]) =>
          category !== 'NONE'
            ? [
                {
                  children: val.sort((a, b) =>
                    (a.headerName?.toLowerCase() ?? '').localeCompare(b.headerName?.toLowerCase() ?? ''),
                  ),
                  headerName: (config?.groupPrefix ?? '') + category,
                },
              ]
            : val,
        ),
        {
          children: [
            PriceColDef({
              colId: buildPorfolioColumns('Cost'),
              field: buildPorfolioColumns('Cost'),
              headerName: 'Cost',
              hide: true,
            }),
            ChangePercentColDef({
              colId: buildPorfolioColumns('GainPercentage'),
              field: buildPorfolioColumns('GainPercentage'),
              headerName: 'Gain (%)',
              hide: true,
            }),
            PriceColDef({
              colId: buildPorfolioColumns('GainDollar'),
              field: buildPorfolioColumns('GainDollar'),
              headerName: 'Gain ($)',
              hide: true,
            }),
            PriceColDef({
              colId: buildPorfolioColumns('MarketValue'),
              field: buildPorfolioColumns('MarketValue'),
              headerName: 'Mrkt Value',
              hide: true,
            }),
            ShortenedNumberColDef({
              colId: buildPorfolioColumns('Quantity'),
              field: buildPorfolioColumns('Quantity'),
              headerName: 'Quantity',
              hide: true,
            }),
          ],
          headerName: 'Holding',
        },
        {
          children: [
            PriceAlertColDef({
              field: 'PriceAlerts',
              headerName: 'Price Alert',
              hide: true,
            }),
            NotesColDef({
              field: 'notes',
              headerName: 'Notes',
              hide: true,
            }),
          ],
          headerName: 'Reminders',
        },
        {
          children: sparklineTimeFrames.map((s, i) =>
            SparkLineColDef(
              {
                field: `sparkline${i}`,
                headerName: s.label,
                hide: true,
              },
              s,
            ),
          ),
          headerName: 'Chart',
        },
      ];

      return newCols;
    },
    [buildPorfolioColumns, config?.groupPrefix, defs, latestTimeConfig, sanitizeColumnState],
  );

  const [columnDefs, setColDefs] = React.useState<(ColGroupDef | ColDef)[]>(() => getColDefs(colLayouts));

  const prevDef = React.useRef<FullDataField[] | undefined>(undefined);
  const prevTimeConfigRef = React.useRef<TimeConfig | null>(latestTimeConfig);
  const prevColLayoutsRef = React.useRef(colLayouts);

  React.useEffect(() => {
    const callback = config?.callback;
    if (
      prevDef.current !== defs ||
      prevTimeConfigRef.current !== latestTimeConfig ||
      !arrayDeepEqual(prevColLayoutsRef.current, colLayouts)
    ) {
      prevDef.current = defs;
      prevTimeConfigRef.current = latestTimeConfig;
      prevColLayoutsRef.current = colLayouts;
      setColDefs(prev => getColDefs(colLayouts, prev));
      if (callback) {
        callback(true);
      }
    }
  }, [defs, getColDefs, colLayouts, latestTimeConfig, config?.callback]);

  return columnDefs;
};

export interface UserSettings {
  timeFormat: TimeFormat;
  timeOffset: number;
  timezone: string;
}
export type TimezoneName = string;

/**
 * Build columns defs based on the data field def.
 * A lot of fairly manual/custom config for columns in here that could use cleaning up in the future.
 */
const buildColDef = (df: FullDataField, colLayout: ColDef, settings: UserSettings): ColDef => {
  const format = df.format.split('-')[0];
  // Our default coldef
  const colDef: ColDef = {
    ...colLayout,
    colId: df.name ?? undefined,
    field: df.name ?? undefined,
    headerName: df.label ?? undefined,
    hide: df.hide ?? true,
    initialWidth: 100,
    sortable: df.filterable ?? undefined,
  };

  if (!colDef.cellRenderer && !colDef.tooltipValueGetter) {
    colDef.tooltipValueGetter = (params: ITooltipParams): string => {
      const text = params.valueFormatted || params.value;
      const cellWidth = params.column?.getActualWidth() || 0;
      return isTextOverflowingAgGridCell(text, cellWidth) ? text : '';
    };
  }

  switch (format) {
    case 'calculatedChangePercent':
      return CalculatedChangePercentColDef(colDef, '', '');
    case 'change':
      return ChangeColDef(colDef);
    case 'changePercent':
      return ChangePercentColDef(colDef);
    case 'changeShortenedNumber':
      return ChangeShortenedNumberColDef(colDef);
    case 'commaSeparatedNumber':
      return CommaSeparatedNumberColDef(colDef);
    case 'date':
      return DateColDef(colDef, {
        timeFormat: settings.timeFormat,
        timeOffset: settings.timeOffset,
        timezone: settings.timezone,
      });
    case 'time':
    case 'dateTime':
      return DateTimeColDef(colDef, {
        timeFormat: settings.timeFormat,
        timeOffset: settings.timeOffset,
        timezone: settings.timezone,
      });

    case 'number':
    case 'positiveNumber':
      return NumberColDef(colDef);
    case 'shortenedNumber':
      return ShortenedNumberColDef(colDef);
    case 'percentNumberBackgroundColor':
      return PercentNumberBackgroundColorColDef(colDef);
    case 'percentNumber':
    case 'positivePercentNumber':
      return PercentNumberColDef(colDef, 1);
    case 'positivePrice':
    case 'price':
      return PriceColDef(colDef);
    case 'boolean':
      return BooleanColDef(colDef);
    case 'flag':
      return FlagColDef(colDef);
    case 'link':
      return LinkColDef(colDef);

    case 'note':
      return NotesColDef(colDef);
    case 'period':
      return PeriodColDef(colDef);
    case 'priceAlert':
      return PriceAlertColDef(colDef);
    case 'progress':
      return ProgressColDef(colDef, { high: 100, low: 0 });
    case 'set':
      return SetColDef(colDef);
    case 'sparkLine':
      return SparkLineColDef(colDef);
    case 'symbol':
      return SymbolColDef(colDef);
    case 'text':
      return TextColDef(colDef);
  }

  // If the data field has options, then use that to map values
  // if ((df.options?.length ?? 0) > 0) {
  //   const optionsByValue = new Map<string, string>(
  //     df.options?.map(option => [option.value as string, option.label as string]),
  //   );
  //   colDef.cellRenderer = (params: { value: any }) => optionsByValue.get(String(params.value)) ?? params.value ?? '-';
  // }

  return colDef;
};
